/*
  Tuna Tubes — real 3D preview.

  Replaces the flat-SVG renderer. The reason for the change is lighting, not
  detail: the client's reference is a Blender-style render whose quality comes
  from soft shadows, ambient fill and graded falloff. Flat SVG polygons paint
  one solid colour per face and cannot produce any of that, so adding facets to
  the SVG had a hard ceiling.

  Orthographic camera on a fixed three-quarter view — no orbit, the framing is
  art-directed. Three.js is VENDORED into the theme's assets (no CDN at
  runtime) and pulled in with a dynamic import, so there is no build step and
  no <script type="importmap"> to collide with Shopify's own tags.

  Reads the same form contract as tuna-config.js: radios carrying data-key /
  data-value. That file still owns the constraint logic and submit gating and
  is untouched — this module only draws.
*/
(async function () {
  'use strict';

  var host = document.querySelector('[data-ttc-scene]');
  var form = document.querySelector('[data-ef-ttc] form');
  if (!host || !form) return;

  var srcUrl = host.getAttribute('data-three');
  if (!srcUrl) return;

  /* Hand back to the flat SVG renderer rather than leaving a blank plate. */
  function fallback() {
    if (window.EFTunaSVG && !window.EFTunaSVG.active) window.EFTunaSVG.fallback();
  }

  /* Check WebGL BEFORE importing — no point pulling 670KB onto a device that
     cannot draw it. Covers GPU blocklists and locked-down browsers. */
  function webglOK() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  if (!webglOK()) { fallback(); return; }

  var THREE;
  try {
    THREE = await import(srcUrl);
  } catch (e) {
    fallback();
    return;
  }

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var renderer;

  try {

  /* ---- palette (identical to the form's tiles and the old renderer) ----- */

  var TUBE_COLORS = { blue: 0x2456A6, green: 0x1E7A46, white: 0xDDE3E9, black: 0x3A3F46 };
  /* THE PLUG CHOICE DOES NOT APPEAR IN THE MODEL. It is sold separately, so
     the fittings are simply the colours the product ships in: the modular
     pump's outlet cap is red, the transom manifold's end connector is black.
     An earlier pass recoloured them per plug type on a misunderstanding. */
  var CAP_RED = 0xC22B2B;
  var WATER = 0x1BAFA9, STEEL = 0x6B7280, PUMP = 0xEDEFF2, WALL = 0x9BA6B2;

  var BLOCKS = { '15': 3, '17.75': 4, '22': 5 };
  /* Sized from the client's brochure, which gives real bore dimensions:
     Small 3-5/16 x 4-3/4in, Medium 5 x 6.5in, Jumbo 6 x 8in. Scene units are
     set so Medium's body reads ~1.8x as tall as it is wide, matching the
     product photo; Small and Jumbo then follow the brochure's ratios.
     The old 15/20/26 gave 2.9 and looked like drainpipe. */
  var RADII = { small: 23, medium: 33, jumbo: 40 };
  var BLOCK_H = 30;

  /* Body height per option. These used to be blocks x BLOCK_H, which welded
     the height to the collar count — so they are explicit now. 17 3/4in is 15%
     taller than that old 120 and 22in is 25% taller than 150, then both were
     taken up a further 10% (138 -> 152, 188 -> 207), all at the client's word.
     ONLY THE STRAIGHT BODY GROWS: the funnel (STEM_H, NECK_H), the modular
     connector and the pump are all separate constants and are untouched. */
  var BODY_H = { '15': 90, '17.75': 152, '22': 207 };

  /* Bolts on the strip, per height. Client's counts, not derived. */
  var BOLTS = { '15': 3, '17.75': 4, '22': 6 };

  /* The neck is a FIXED part of the product — only the body grows with the
     height option — so these stay constants. In the photo the taper plus stem
     is roughly 40% of all the blue; 50 + 36 against bodies of 90/120/150 gives
     49% / 42% / 36% across the three heights. The pump is a real bilge pump
     and scales with the tube it bolts to. */
  /* The taper is SHORT and happens EARLY — a slight pinch just under the
     braced body — and below it the tube runs STRAIGHT at that width all the
     way to the pump. Two earlier passes got this wrong in opposite directions:
     first a hard 0.38 bottleneck, then a long gradual cone. In the side-on
     photo the silhouette steps in once and then stays parallel. */
  var STEM_R = 0.8;
  var NECK_H = 20, STEM_H = 64;

  /* Below the initial step the tube is NOT parallel after all — it keeps
     tapering, very gently, all the way to the pump. This is the radius at the
     very bottom, as a percentage of the tube radius, and it is tunable because
     the difference is small enough to be worth eyeballing. */
  var STEM_R_BOT = parseFloat(host.getAttribute('data-stem-bottom'));
  if (isNaN(STEM_R_BOT)) STEM_R_BOT = 65;

  /* Gap between adjacent tubes, in scene units, MODULAR ONLY — a transom bank
     is spaced by its manifold tees, so it keeps the fixed 24. */
  var TUBE_GAP = parseFloat(host.getAttribute('data-tube-gap'));
  if (isNaN(TUBE_GAP)) TUBE_GAP = 0;

  /* The brochure gives two bore dimensions per size — 5 x 6.5in on a Medium —
     so the real bore is an OVAL at about 1:1.35. The client asked for oval but
     "roundish", so this is pulled back to 1.22: clearly not a circle, not a
     flattened tube. The long axis runs in Z, away from the transom, which is
     also how it sits on a boat. The pump is a real bilge pump and is NOT
     stretched with it. */
  var Z_RATIO = 1.22;

  /* The intake strainer is an off-the-shelf bilge-pump part and is always this
     blue, whatever colour the tube is — so it does NOT follow the colour
     option. Same on both mounts: every tube carries the same pump. */
  var PUMP_BLUE = 0x1E4FA0;

  /* Transom manifold. The horizontal pipe is MUCH fatter than the risers that
     feed it — in the client's photo the riser steps out into the tee and the
     run is roughly 1.7x its diameter. These were one shared value and so came
     out the same width; they are separate now, and both functions read these
     rather than recomputing, because they must agree. */
  function riserR(r) { return r * 0.26; }
  function pipeR(r) { return r * 0.44; }

  /* The face the detail sits on. The bolt strip and the pump intake both use
     it, so they always point the same way — the client set this by eye at 15
     degrees, walked left from 75 through 45 and 30. */
  var FACE_TH = Math.PI / 180 * 15;

  /* The pump intake used to share FACE_TH with the bolt strip. It is split out
     so it can be aimed independently, seeded from the section setting and
     overridden live by the tuning slider (see the bottom of this file). */
  var PUMP_DEG = parseFloat(host.getAttribute('data-pump-angle'));
  if (isNaN(PUMP_DEG)) PUMP_DEG = 85;

  /* Length of the pump's white body, as a percentage of the tube radius. In
     the client's photo the whole pump is only slightly wider than the tube, so
     this is far shorter than it looks like it should be. */
  var PUMP_LEN = parseFloat(host.getAttribute('data-pump-length'));
  if (isNaN(PUMP_LEN)) PUMP_LEN = 105;

  /* Length of the blue intake strainer, also as a percentage of the tube
     radius. Separate from the body so the two can be balanced against each
     other — the drum, its teeth and its hub all scale off this. */
  var STRAINER_LEN = parseFloat(host.getAttribute('data-strainer-length'));
  if (isNaN(STRAINER_LEN)) STRAINER_LEN = 25;
  function pumpH(r) { return r * 0.95; }
  function asmHeight(r) { return pumpH(r) + STEM_H + NECK_H; }

  /* Facet count. The client's reference render reads about twelve sides —
     faceted enough to be unmistakably low-poly, round enough to be a tube.
     Six (the old hex) is one number away if that is preferred. */
  var SIDES = 12;

  /* ---- renderer -------------------------------------------------------- */

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  if ('outputColorSpace' in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.setAttribute('aria-hidden', 'true');
  renderer.domElement.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;display:block;';

  var scene = new THREE.Scene();
  var camera = new THREE.OrthographicCamera(-100, 100, 100, -100, -2000, 2000);
  /* Fixed three-quarter view at ~19 degrees of elevation — the client picked
     this angle over a higher one. Held as a CONSTANT rather than derived from
     the model centre: deriving it made the angle depend on build height, so a
     5-block tower silently flattened the view that a 3-block one framed well. */
  var VIEW_DIR = new THREE.Vector3(1, 0.48, 1).normalize();

  /* Studio lighting: a key that casts, a fill from the opposite side, and a
     hemisphere for the soft ambient that makes white-on-white legible. */
  var hemi = new THREE.HemisphereLight(0xffffff, 0xdfe5ec, 0.62);
  scene.add(hemi);

  var key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(-160, 300, 190);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.radius = 5;
  key.shadow.bias = -0.0022;
  scene.add(key);
  scene.add(key.target);

  var fill = new THREE.DirectionalLight(0xffffff, 0.34);
  fill.position.set(220, 120, -160);
  scene.add(fill);

  /* Shadow catcher — invisible plane, shadow only, over the white plate. */
  var ground = new THREE.Mesh(
    new THREE.PlaneGeometry(4000, 4000),
    new THREE.ShadowMaterial({ opacity: 0.16 })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  var root = new THREE.Group();
  scene.add(root);

  /* ---- ribbing texture -------------------------------------------------
     The X-brace and bolt dots ride a canvas bump map rather than geometry:
     six tubes of real ribs would be hundreds of meshes, and a bump map still
     catches the key light, which is the whole point of moving to 3D. */
  var bumpTex = (function () {
    var c = document.createElement('canvas');
    c.width = 512; c.height = 256;
    var x = c.getContext('2d');
    x.fillStyle = '#808080';
    x.fillRect(0, 0, 512, 256);
    x.strokeStyle = '#ffffff';
    x.lineWidth = 11;
    x.lineCap = 'round';
    for (var p = 0; p < 4; p++) {
      var ox = p * 128;
      x.beginPath();
      x.moveTo(ox + 26, 34); x.lineTo(ox + 102, 222);
      x.moveTo(ox + 102, 34); x.lineTo(ox + 26, 222);
      x.stroke();
      x.strokeRect(ox + 22, 30, 84, 196);
    }
    x.fillStyle = '#2b2b2b';
    for (var d = 0; d < 4; d++) {
      x.beginPath();
      x.arc(d * 128 + 64, 128, 9, 0, Math.PI * 2);
      x.fill();
    }
    var t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
  })();

  /* ---- material + geometry bookkeeping --------------------------------- */

  var pool = [];
  function track(o) { pool.push(o); return o; }

  function mat(color, opts) {
    var o = Object.assign({ color: color, roughness: 0.72, metalness: 0.0, flatShading: true }, opts || {});
    return track(new THREE.MeshStandardMaterial(o));
  }

  function disposeAll() {
    root.traverse(function (n) { if (n.isMesh) n.geometry && n.geometry.dispose && 0; });
    while (root.children.length) root.remove(root.children[0]);
    pool.forEach(function (o) { o.dispose && o.dispose(); });
    pool = [];
  }

  function cyl(rt, rb, h, seg, open) {
    return track(new THREE.CylinderGeometry(rt, rb, h, seg || SIDES, 1, !!open));
  }

  function meshAt(geo, material, x, y, z) {
    var m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z);
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  }

  /* ---- build one tube --------------------------------------------------- */

  /* Pump intake strainer. From the client's end-on photo: the slots are RADIAL
     TEETH ON THE FACE, like a vented wheel — not ribs running along the drum,
     which is what an earlier pass built off the side-on shot. A dark recess
     sits behind them so the gaps read as openings rather than grooves, and a
     smooth raised hub fills the centre.

     Built along local X with -X as the outward face; the caller rotates the
     whole pump to aim it. Teeth sweep the YZ circle, so a tooth at angle a
     sits at (cos a, sin a) in YZ and rotation.x = a turns its local +Y radial. */
  function buildStrainer(g, cx, cy, R, L, mat, hubMat) {
    var faceX = cx - L * 0.5;

    var drum = meshAt(cyl(R * 0.9, R * 0.9, L, 24), mat, cx, cy, 0);
    drum.rotation.z = Math.PI / 2;
    g.add(drum);

    /* Dark recess just inside the face — without it the teeth read as ribs on
       a solid disc instead of a grille with holes in it. */
    var recess = meshAt(cyl(R * 0.84, R * 0.84, L * 0.16, 24), hubMat, faceX + L * 0.08, cy, 0);
    recess.rotation.z = Math.PI / 2;
    g.add(recess);

    var rim = meshAt(cyl(R, R, L * 0.3, 24), mat, faceX + L * 0.02, cy, 0);
    rim.rotation.z = Math.PI / 2;
    g.add(rim);

    var toothG = track(new THREE.BoxGeometry(L * 0.3, R * 0.42, R * 0.085));
    var tr = R * 0.62;
    for (var i = 0; i < 28; i++) {
      var a = (i / 28) * Math.PI * 2;
      var tooth = meshAt(toothG, mat, faceX - L * 0.08,
        cy + Math.cos(a) * tr, Math.sin(a) * tr);
      tooth.rotation.x = a;
      g.add(tooth);
    }

    var hub = meshAt(cyl(R * 0.38, R * 0.38, L * 0.46, 20), mat, faceX - L * 0.14, cy, 0);
    hub.rotation.z = Math.PI / 2;
    g.add(hub);
  }

  function buildTube(r, blocks, bodyH, nBolts, mount, bodyMat, bodyDarkMat, capMat, steelMat, pumpMat, waterMat, dotMat, flatMat, strainerMat, pvcMat, fittingMat) {
    var g = new THREE.Group();

    /* Local ASM_H shadows the module scope deliberately: the height from the
       ground to the base of the blue stem depends on the mount, and on a
       modular bank it also scales with the tube's radius. */
    var MR = r * 0.26;
    var PH;

    if (mount === 'modular') {
      /* Each tube gets its own bilge pump: white body lying across, blue
         intake strainer, plug-colour cap. In the photo the pump is slightly
         WIDER than the tube — that is what gives the base its weight. */
      /* The tube does not sit straight on the pump. As on the transom riser,
         it steps down through a short taper into a narrow connector, and the
         pump hangs below that — which is what puts daylight between the intake
         and the tube instead of jamming them together. */
      /* PUMP_H is the pump's OWN height and is what every pump dimension sizes
         off. PH is the height from the ground to the base of the blue stem —
         a different thing, and taller since the connector went in. Conflating
         them inflated the pump and its intake the moment the connector was
         added, because bodyR and capR were reading PH. */
      var PUMP_OWN_H = pumpH(r);
      var connR = r * 0.34, connH = r * 0.34, connTaperH = r * 0.26;
      PH = PUMP_OWN_H + connH + connTaperH;

      var connY = PUMP_OWN_H;
      g.add(meshAt(cyl(connR, connR, connH), fittingMat, 0, connY + connH / 2, 0));

      var connTaper = meshAt(cyl(r * (STEM_R_BOT / 100), connR, connTaperH), flatMat,
        0, connY + connH + connTaperH / 2, 0);
      connTaper.scale.z = Z_RATIO;
      g.add(connTaper);

      /* The whole pump is turned to FACE_TH, so the intake looks out of the
         same face the bolt strip sits on and you see it end-on rather than
         edge-on. Built along local X inside the group; the group does the aim. */
      var pumpG = new THREE.Group();
      pumpG.rotation.y = Math.PI / 180 * PUMP_DEG;

      /* A bilge pump is a CYLINDER, not a box — the earlier BoxGeometry read as
         a random grey crate sitting on the rack. Round white body, blue intake
         drum one end, plug-colour cap the other, all sharing one axis. */
      var pw = r * (PUMP_LEN / 100);
      var bodyR = PUMP_OWN_H * 0.44;
      var capR = PUMP_OWN_H * 0.5, capL = r * (STRAINER_LEN / 100);
      var pumpY = PUMP_OWN_H / 2;

      var pumpBody = meshAt(cyl(bodyR, bodyR, pw, 20), pumpMat, 0, pumpY, 0);
      pumpBody.rotation.z = Math.PI / 2;
      pumpG.add(pumpBody);

      /* a raised collar where the body meets the intake, as in the photo */
      var collarP = meshAt(cyl(bodyR * 1.1, bodyR * 1.1, pw * 0.12, 20), pumpMat,
        -pw * 0.34, pumpY, 0);
      collarP.rotation.z = Math.PI / 2;
      pumpG.add(collarP);

      buildStrainer(pumpG, -(pw / 2 + capL / 2) + 3, pumpY, capR, capL, strainerMat, dotMat);

      var capG = cyl(capR * 0.86, capR * 0.86, capL, 16);
      var cap = meshAt(capG, capMat, (pw / 2 + capL / 2) - 3, pumpY, 0);
      cap.rotation.z = Math.PI / 2; pumpG.add(cap);
      g.add(pumpG);
    } else {
      /* A transom bank plumbs into ONE shared PVC manifold instead — no pump
         per tube. This part is the vertical riser and its collar nut; the
         horizontal pipe, the tees and the end couplings span the whole row and
         are built once in build(). */
      var PR = pipeR(r);
      var riserH = r * 0.55;
      /* The riser rises from the pipe's centreline, so its base is buried in
         the run and reads as plumbed into it rather than balanced on top. */
      PH = PR + riserH + MR * 0.7;
      g.add(meshAt(cyl(MR * 0.95, MR * 1.15, riserH, 14), pvcMat, 0, PR + riserH / 2, 0));
      g.add(meshAt(cyl(MR * 1.35, MR * 1.35, MR * 0.7, 12), fittingMat,
        0, PR + riserH + MR * 0.35, 0));
    }

    var ASM_H = PH + STEM_H + NECK_H;

    /* Everything above the pump takes the oval. CylinderGeometry is circular,
       so the cross-section comes from a non-uniform scale on Z — safe here
       because these are all unrotated Y-axis cylinders. */
    var stem = meshAt(cyl(r * STEM_R, r * (STEM_R_BOT / 100), STEM_H), flatMat,
      0, PH + STEM_H / 2, 0);
    stem.scale.z = Z_RATIO; g.add(stem);
    var neck = meshAt(cyl(r, r * STEM_R, NECK_H), flatMat, 0, PH + STEM_H + NECK_H / 2, 0);
    neck.scale.z = Z_RATIO; g.add(neck);

    /* body, open-ended so the bore is visible from above */
    var body = meshAt(cyl(r, r, bodyH, SIDES, true), bodyMat, 0, ASM_H + bodyH / 2, 0);
    body.material.side = THREE.DoubleSide;
    body.scale.z = Z_RATIO;
    g.add(body);

    /* A lip where the braced body meets the taper — there is a clear step
       there in the side-on photo, and without it the two read as one cone. */
    var lip = meshAt(cyl(r * 1.05, r * 1.05, 4.2), flatMat, 0, ASM_H + 1, 0);
    lip.scale.z = Z_RATIO; g.add(lip);

    /* collar rings mark the block boundaries — the client's "blocks" language */
    for (var b = 1; b < blocks; b++) {
      var collar = meshAt(cyl(r * 1.045, r * 1.045, 3.4), flatMat,
        0, ASM_H + b * (bodyH / blocks), 0);
      collar.scale.z = Z_RATIO;
      g.add(collar);
    }

    /* bore: dark inner wall, water surface sunk 10 below the rim, rim annulus.
       The disc and the ring sit in XY and are laid flat by rotation.x, so for
       THEM the oval is a scale on local Y — scale is applied before rotation,
       and local +Y maps to world Z once rotated. */
    var topY = ASM_H + bodyH;
    var bore = meshAt(cyl(r * 0.80, r * 0.80, bodyH * 0.9, SIDES, true), bodyDarkMat,
      0, ASM_H + bodyH * 0.55, 0);
    bore.scale.z = Z_RATIO;
    bore.castShadow = false;
    g.add(bore);
    /* The water takes no part in shadowing. It sits 10 below the rim inside a
       dark bore, so the rim and the far wall both cast onto it — correct, but
       at this size it reads as a dirty smudge across the surface rather than
       depth. The dark bore behind it already sells the recess. */
    var water = meshAt(track(new THREE.CircleGeometry(r * 0.80, SIDES)), waterMat, 0, topY - 10, 0);
    water.rotation.x = -Math.PI / 2;
    water.scale.y = Z_RATIO;
    water.castShadow = false;
    water.receiveShadow = false;
    g.add(water);
    var rim = meshAt(track(new THREE.RingGeometry(r * 0.80, r, SIDES)), flatMat, 0, topY, 0);
    rim.rotation.x = -Math.PI / 2;
    rim.scale.y = Z_RATIO;
    g.add(rim);

    /* Raised bolt strip, from the client's reference: a vertical panel on one
       face carrying four bolt heads. Real geometry rather than more bump map —
       it needs a silhouette and a shadow, which a bump map cannot give.

       CylinderGeometry lays out x = r*sin(theta), z = r*cos(theta), so theta 0
       is +Z and the outward normal at theta is (sin, 0, cos). A child group
       rotated by rotation.y = theta therefore has its local +Z pointing out of
       the face, which makes the panel and its bolts plain box/cylinder work in
       local space. The camera faces theta 45deg and theta INCREASING runs to
       screen-right. The panel sits at 15deg, walked left from 75 by eye across
       45 and 30. Settled by the client; do not "correct" it.
       On an OVAL the outward normal is no longer the angle itself: for semi-axes
       (rx, rz) it runs (sin t / rx, cos t / rz), so the panel has to be aimed
       by that rather than by t, or it sits skewed against the surface. */
    var th = FACE_TH;
    var rx = r, rz = r * Z_RATIO;
    var nx = Math.sin(th) / rx, nz = Math.cos(th) / rz;
    var nlen = Math.sqrt(nx * nx + nz * nz);
    nx /= nlen; nz /= nlen;

    var panel = new THREE.Group();
    panel.position.set(
      rx * Math.sin(th) + nx * 1.4,
      ASM_H + bodyH * 0.5,
      rz * Math.cos(th) + nz * 1.4
    );
    panel.rotation.y = Math.atan2(nx, nz);

    /* NOT `pw` — that is the pump's width in this same function scope, and a
       second `var pw` silently clobbered it. */
    var panW = r * 0.52, ph = bodyH * 0.68;
    panel.add(meshAt(track(new THREE.BoxGeometry(panW, ph, 3)), flatMat, 0, 0, 0));

    var boltG = cyl(1.9, 1.9, 1.8, 8);
    var boltGap = ph / (nBolts + 0.8);
    for (var d = 0; d < nBolts; d++) {
      var bolt = meshAt(boltG, dotMat, 0, (d - (nBolts - 1) / 2) * boltGap, 1.9);
      bolt.rotation.x = Math.PI / 2;
      panel.add(bolt);
    }
    g.add(panel);

    return g;
  }

  /* ---- state ------------------------------------------------------------ */

  function checked(k) {
    var el = form.querySelector('input[data-key="' + k + '"]:checked');
    return el ? el.getAttribute('data-value') : null;
  }

  var prevKey = null;
  var tunePanel = null;
  var lastMount = null;
  /* The transom manifold's two materials, kept so ticking the Manifold add-on
     can restyle them without a rebuild. Null on a modular bank. */
  var manMats = null;
  var manOn = null;
  var pulseRaf = 0;

  /* The only tunable left is modular-only, so the panel has nothing to say on
     a transom bank. */
  function syncTuneVisibility() {
    if (tunePanel) tunePanel.style.display = (lastMount === 'modular') ? '' : 'none';
  }

  /* Heights vary per tube as well as widths — a 17¾ and a 22 can share a bank.
     Both fall back to a single value when the slot controls are absent, which
     covers first paint and JS-off. */
  function slotHeights(n, mount) {
    var legal = { transom: ['17.75', '22'], modular: ['15', '17.75'] }[mount] || ['17.75'];
    var sels = form.querySelectorAll('[data-key="slotheight"]');
    var out = [];
    for (var i = 0; i < n; i++) {
      var v = sels[i] ? sels[i].value : legal[0];
      out.push(legal.indexOf(v) === -1 ? legal[0] : v);
    }
    return out;
  }

  function slotWidths(n, fallback, heights, mount) {
    var sels = form.querySelectorAll('[data-key="slot"]');
    var out = [];
    for (var i = 0; i < n; i++) {
      var v = sels[i] ? sels[i].value : fallback;
      /* Small is judged on THIS tube's height, not the bank's. */
      if (v === 'small' && !(mount === 'transom' && heights[i] === '17.75')) v = 'medium';
      out.push(RADII[v] ? v : fallback);
    }
    return out;
  }

  function build() {
    var mount = checked('mount') || 'transom';
    var tubes = parseInt(checked('tubes'), 10) || 3;
    var heights = slotHeights(tubes, mount);
    var height = heights[0];
    var width = checked('width') || 'medium';
    /* A bank can MIX sizes. The setup people actually run in competition is
       jumbo at both ends with medium in the middle, so width is per slot, and
       the layout below is a cumulative walk rather than a uniform stride.
       Falls back to the single Width radio whenever the slot controls are
       absent — they are JS-built, so that covers first paint and JS-off. */
    var widths = slotWidths(tubes, width, heights, mount);
    var color = checked('color') || 'blue';
    /* Square only exists on a modular 4 or 6 — anything else draws a line,
       whatever a stale radio says. Matches the form gating. */
    var layout = checked('layout') || 'line';
    if (!(mount === 'modular' && (tubes === 4 || tubes === 6))) layout = 'line';

    lastMount = mount;
    syncTuneVisibility();

    /* `plugs` and `suckers` are deliberately absent — neither changes anything
       drawn, so choosing one should not trigger a rebuild. */
    var stateKey = [mount, tubes, heights.join(','), widths.join(','), color, layout,
                    PUMP_DEG, PUMP_LEN, STRAINER_LEN, STEM_R_BOT, TUBE_GAP].join('|');
    if (stateKey === prevKey) return;
    prevKey = stateKey;

    disposeAll();

    var rs = widths.map(function (w) { return RADII[w]; });
    /* Everything that frames the WHOLE bank — the wall, the rack, the
       manifold — sizes off the widest tube, so `r` keeps its old meaning and
       none of that code below has to change. Only the tubes themselves and
       their X positions are per-slot. (The sucker pads used to be in this list
       and are no longer drawn at all.) */
    var r = Math.max.apply(null, rs);
    /* Per tube now. The bank-level pieces — wall, rack, manifold — size off the
       TALLEST, exactly as they size off the widest. */
    var blocksArr = heights.map(function (h) { return BLOCKS[h]; });
    var bodyHArr = heights.map(function (h) { return BODY_H[h]; });
    var boltsArr = heights.map(function (h) { return BOLTS[h]; });
    var bodyH = Math.max.apply(null, bodyHArr);
    var blocks = BLOCKS[height];
    var nBolts = BOLTS[height];
    var ASM_H = asmHeight(r);

    /* Keep the X-brace density constant as the body grows, or a 22in tube
       wears one stretched brace instead of a repeating pattern. */
    bumpTex.repeat.set(1, bodyH / 90);
    bumpTex.needsUpdate = true;
    var gap = (mount === 'modular' ? TUBE_GAP : 24);
    /* Square: columns run along X as before, the second row sits behind in
       Z. The Z pitch is wider than the X pitch because the bore is an oval
       stretched by Z_RATIO. Transom never gets here — layout is forced to
       'line' above. */
    var cols = layout === 'square' ? tubes / 2 : tubes;
    var rows = layout === 'square' ? 2 : 1;
    var zSpacing = r * 2 * Z_RATIO + TUBE_GAP;

    /* A column is as wide as the widest tube in it, so a jumbo behind a medium
       still clears its neighbour. */
    var colR = [];
    for (var c = 0; c < cols; c++) {
      var m = 0;
      for (var rw = 0; rw < rows; rw++) {
        var ix = rw * cols + c;
        if (ix < tubes) m = Math.max(m, rs[ix]);
      }
      colR.push(m);
    }

    /* Cumulative, NOT `i * spacing`: with mixed widths every neighbouring pair
       has its own centre-to-centre distance, so a uniform stride would either
       overlap the jumbos or strand the mediums. */
    var colX = [];
    var run = 0;
    for (var cx = 0; cx < cols; cx++) {
      if (cx > 0) run += colR[cx - 1] + gap + colR[cx];
      colX.push(run);
    }
    var rowW = run;                      /* first centre to last centre */
    var x0 = -rowW / 2;
    for (var cc = 0; cc < cols; cc++) colX[cc] += x0;

    var bodyMat = mat(TUBE_COLORS[color], { bumpMap: bumpTex, bumpScale: 1.6 });
    var bodyDark = mat(TUBE_COLORS[color], { roughness: 0.95, side: THREE.DoubleSide });
    bodyDark.color.multiplyScalar(0.42);
    var capMat = mat(CAP_RED);
    var steelMat = mat(STEEL, { roughness: 0.55, metalness: 0.25 });
    var pumpMat = mat(PUMP, { roughness: 0.6 });
    var waterMat = mat(WATER, { roughness: 0.25, metalness: 0.1 });
    var dotMat = mat(0x161C24, { roughness: 0.9 });
    /* bodyMat carries the X-brace bump map and belongs ONLY on the braced
       body cylinder. Everything else — funnel, stem, lip, collars, rim, bolt
       plate — takes flatMat, or the braces smear across surfaces that are
       smooth on the real product. They are cast into the body, not a finish
       applied to the whole tube. */
    var flatMat = mat(TUBE_COLORS[color]);
    var strainerMat = mat(PUMP_BLUE, { roughness: 0.55 });
    var pvcMat = mat(0x6E7377, { roughness: 0.78 });
    var fittingMat = mat(0x2B2F33, { roughness: 0.68 });
    /* On a transom bank these two materials are ONLY the manifold — pipe,
       tees, risers, collars, couplings, end connector — so restyling them
       restyles exactly the part the add-on sells. On modular fittingMat is
       also the pump connector, which is why this is transom-only. */
    manMats = mount === 'transom' ? [pvcMat, fittingMat] : null;
    manOn = null;

    for (var i = 0; i < tubes; i++) {
      var t = buildTube(rs[i], blocksArr[i], bodyHArr[i], boltsArr[i], mount, bodyMat, bodyDark, capMat, steelMat, pumpMat, waterMat, dotMat, flatMat, strainerMat, pvcMat, fittingMat);
      t.position.x = colX[i % cols];
      t.position.z = (Math.floor(i / cols) - (rows - 1) / 2) * zSpacing;
      root.add(t);
    }

    /* mount structure — transom bolts to the boat's back wall, modular is a
       freestanding deck rack. Real product difference; keep both. */
    if (mount === 'transom') {
      var wallMat = mat(WALL, { roughness: 0.85 });
      var wallH = ASM_H + bodyH * 0.86;
      root.add(meshAt(track(new THREE.BoxGeometry(rowW + r * 2 + 36, wallH, 8)),
        wallMat, 0, wallH / 2, -r - 19));

      /* Shared PVC manifold: ONE pipe feeding the whole bank, a tee under each
         riser, a coupling at both ends. This is what a transom bank actually
         plumbs into — there is no pump per tube here, unlike modular. MR must
         match the riser radius in buildTube. */
      var PR = pipeR(r);
      var manLen = rowW + r * 1.8;
      var pipe = meshAt(cyl(PR, PR, manLen, 18), pvcMat, 0, PR, 0);
      pipe.rotation.z = Math.PI / 2;
      root.add(pipe);

      for (var ti = 0; ti < tubes; ti++) {
        var tee = meshAt(cyl(PR * 1.2, PR * 1.2, PR * 1.5, 18), pvcMat,
          colX[ti], PR, 0);
        tee.rotation.z = Math.PI / 2;
        root.add(tee);
      }

      [-1, 1].forEach(function (sgn) {
        var coup = meshAt(cyl(PR * 1.26, PR * 1.26, PR * 1.15, 18), fittingMat,
          sgn * (manLen / 2 + PR * 0.5), PR, 0);
        coup.rotation.z = Math.PI / 2;
        root.add(coup);
      });

      /* End connector — black, like the rest of the manifold's fittings. */
      var conn = meshAt(cyl(PR * 0.5, PR * 0.5, PR * 1.5, 12), fittingMat,
        manLen / 2 + PR * 1.85, PR, 0);
      conn.rotation.z = Math.PI / 2;
      root.add(conn);
    } else {
      var rackMat = mat(STEEL, { roughness: 0.5, metalness: 0.3 });
      root.add(meshAt(track(new THREE.BoxGeometry(rowW + r * 2 + 40, 12,
        (rows - 1) * zSpacing + r * 2 + 34)), rackMat, 0, -6, 0));
    }

    /* Sea Suckers are an ADD-ON and are NOT modelled. They used to draw two
       0x11181F pads, which read as a black object stuck to the assembly rather
       than as a mounting accessory. Same rule the plug choice already follows:
       sold separately, absent from the model. */

    syncManifold();
    frame();
  }

  /* ---- manifold add-on: ghost when unticked, solid + pulse when ticked ----

     The manifold is an optional add-on, so an unticked transom bank shows it
     pale and SLIGHTLY see-through. Ticking it restores the real colours, makes
     it opaque and gives one short teal glow so the change is noticed; the glow
     is skipped under reduced motion. Pale meshes stop casting shadows.

     A plain transparent material was tried first and rejected by the client:
     the tees, couplings and risers buried inside the run all showed through
     and it turned into overlapping glass blobs. So each manifold mesh carries
     a DEPTH-ONLY twin (colour writes off) drawn just before it in the
     transparent pass. The twins lay down the manifold's nearest surface, and
     the see-through colour then lands only on that surface — it reads as one
     hollow-looking pipe, not a stack of parts. Both sit in the transparent
     pass, after every opaque mesh, so the wall and tubes behind still show. */
  function manifoldTicked() {
    var m = form.querySelector('input[data-key="manifold"]');
    return !!(m && m.checked && !m.disabled);
  }

  var MAN_OPACITY = 0.6;

  function syncManifold() {
    if (!manMats) return;
    var on = manifoldTicked();
    if (on === manOn) return;
    var wasSet = manOn !== null;
    manOn = on;
    manMats.forEach(function (m) {
      if (m.userData.hex === undefined) m.userData.hex = m.color.getHex();
      m.color.setHex(m.userData.hex);
      if (!on) m.color.lerp(new THREE.Color(0xFFFFFF), 0.72);
      m.emissive.setHex(0x000000);
      m.transparent = !on;
      m.opacity = on ? 1 : MAN_OPACITY;
      m.needsUpdate = true;
    });
    var meshes = [];
    root.traverse(function (n) {
      if (n.isMesh && manMats.indexOf(n.material) !== -1) meshes.push(n);
    });
    var depthMat = null;
    meshes.forEach(function (n) {
      n.castShadow = on;
      n.renderOrder = 2;
      var twin = n.userData.depthTwin;
      if (!twin && !on) {
        depthMat = depthMat || track(new THREE.MeshBasicMaterial({ colorWrite: false, transparent: true }));
        twin = new THREE.Mesh(n.geometry, depthMat);
        twin.renderOrder = 1;
        n.add(twin);
        n.userData.depthTwin = twin;
      }
      if (twin) twin.visible = !on;
    });
    if (on && wasSet && !reduced) pulseManifold();
  }

  function pulseManifold() {
    cancelAnimationFrame(pulseRaf);
    var mats = manMats, t0 = performance.now(), UP = 160, DOWN = 640;
    (function step(now) {
      if (mats !== manMats) return;          /* rebuilt mid-pulse */
      var t = now - t0, k;
      if (t < UP) k = t / UP;
      else k = Math.max(0, 1 - (t - UP) / DOWN);
      k = k * k * (3 - 2 * k);               /* smoothstep */
      mats.forEach(function (m) {
        m.emissive.setHex(0x1BAFA9);
        m.emissiveIntensity = 0.75 * k;
      });
      render();
      if (t < UP + DOWN) pulseRaf = requestAnimationFrame(step);
      else mats.forEach(function (m) { m.emissive.setHex(0x000000); });
    })(t0);
  }

  /* ---- framing: fit the ortho camera to whatever was just built --------- */

  function frame() {
    var box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) return;
    var c = box.getCenter(new THREE.Vector3());
    root.position.y -= 0;
    camera.lookAt(c);
    key.target.position.copy(c);
    key.target.updateMatrixWorld();

    /* Project the eight corners onto the camera basis to get a true fit. */
    var dir = VIEW_DIR.clone();
    var right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0)).normalize();
    var up = new THREE.Vector3().crossVectors(right, dir).normalize();
    var minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    var mn = box.min, mx = box.max, v = new THREE.Vector3();
    [mn.x, mx.x].forEach(function (X) {
      [mn.y, mx.y].forEach(function (Y) {
        [mn.z, mx.z].forEach(function (Z) {
          v.set(X, Y, Z).sub(c);
          var u = v.dot(right), w = v.dot(up);
          if (u < minU) minU = u; if (u > maxU) maxU = u;
          if (w < minV) minV = w; if (w > maxV) maxV = w;
        });
      });
    });

    var rect = host.getBoundingClientRect();
    var aspect = Math.max(rect.width, 1) / Math.max(rect.height, 1);
    var pad = 1.14;
    var halfW = Math.max((maxU - minU) / 2, 1) * pad;
    var halfH = Math.max((maxV - minV) / 2, 1) * pad;
    if (halfW / halfH < aspect) halfW = halfH * aspect; else halfH = halfW / aspect;

    var cu = (minU + maxU) / 2, cv = (minV + maxV) / 2;
    camera.left = cu - halfW; camera.right = cu + halfW;
    camera.top = cv + halfH; camera.bottom = cv - halfH;
    camera.position.copy(c).add(VIEW_DIR.clone().multiplyScalar(900));
    camera.lookAt(c);
    camera.updateProjectionMatrix();

    var span = Math.max(halfW, halfH) * 2.4;
    key.shadow.camera.left = -span; key.shadow.camera.right = span;
    key.shadow.camera.top = span; key.shadow.camera.bottom = -span;
    key.shadow.camera.near = 1; key.shadow.camera.far = 2400;
    key.position.copy(c).add(new THREE.Vector3(-260, 460, 300));
    key.shadow.camera.updateProjectionMatrix();

    ground.position.y = box.min.y - 0.5;
    resize();
  }

  function resize() {
    var rect = host.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    renderer.setSize(rect.width, rect.height, false);
    render();
  }

  function render() { renderer.render(scene, camera); }

  /* ---- boot ------------------------------------------------------------- */

  var ph = host.querySelector('[data-ttc-placeholder]');
  if (ph) ph.remove();
  var waves = host.querySelector('.ef-ttc__waves');
  if (waves) waves.remove();
  var oldSvg = host.querySelector('svg');
  if (oldSvg) oldSvg.remove();
  host.appendChild(renderer.domElement);

  form.addEventListener('change', function (e) {
    build();
    syncManifold();
    var isManifold = e.target && e.target.getAttribute &&
      e.target.getAttribute('data-key') === 'manifold';
    /* The manifold has its own pulse; the whole-canvas settle on top of it
       would dim the very thing being pointed at. */
    if (!reduced && !isManifold) {
      /* brief settle so a change reads as a change */
      renderer.domElement.animate(
        [{ opacity: 0.55 }, { opacity: 1 }], { duration: 190, easing: 'ease-out' }
      );
    }
    render();
  });

  if (window.ResizeObserver) new ResizeObserver(function () { frame(); }).observe(host);
  else window.addEventListener('resize', function () { frame(); });

  build();
  syncManifold();
  render();
  if (window.EFTunaSVG) window.EFTunaSVG.active = true;

  /* Tuning slider. Only with ?tune in the query string — this is a workshop
     control, not a customer one. Whatever number it lands on goes into the
     section's "Pump intake angle" setting to persist. */
  /* Tuning panel. Only with ?tune in the query string — a workshop control,
     not a customer one. Each entry writes straight into the module variable it
     names and redraws; whatever the numbers land on go into the matching
     section settings to persist. */
  if (/[?&]tune\b/.test(location.search)) {
    var panel = document.createElement('div');
    panel.style.cssText =
      'position:absolute;left:12px;bottom:12px;z-index:5;background:rgba(255,255,255,.95);' +
      'border:1px solid #ccd6e0;border-radius:8px;padding:9px 11px;' +
      'font:12px/1.5 Montserrat,system-ui,sans-serif;color:#103357;' +
      'box-shadow:0 2px 10px rgba(16,51,87,.16)';

    /* Trimmed to the one control the client is working on. The pump values
       are settled and live in the section settings; add them back here by
       restoring their CONTROLS entries if they need another pass. */
    var CONTROLS = [
      { label: 'Space between tubes', unit: '', min: 0, max: 120, step: 2,
        get: function () { return TUBE_GAP; },
        set: function (v) { TUBE_GAP = v; } },
    ];

    CONTROLS.forEach(function (c) {
      var row = document.createElement('div');
      row.style.cssText = 'margin-bottom:6px';

      var label = document.createElement('div');
      label.innerHTML = c.label + ' <b>' + c.get() + c.unit + '</b>';

      var slider = document.createElement('input');
      slider.type = 'range';
      /* Chrome restores form-control values across reloads AND fires `input`
         while doing it, which silently dragged these away from the stored
         settings on every fresh load. */
      slider.setAttribute('autocomplete', 'off');
      slider.min = String(c.min); slider.max = String(c.max); slider.step = String(c.step);
      slider.value = String(c.get());
      slider.style.cssText = 'width:190px;display:block;margin:4px 0 0';

      /* Only a real interaction counts. `autocomplete="off"` was not enough on
         its own — a slider still drifted away from its stored setting between
         page loads, and because the handler also writes the MODEL value, that
         silently moved the geometry, not just the handle. Anything that fires
         `input` without the user having touched the control gets reverted to
         the authoritative value instead. */
      var touched = false;
      ['pointerdown', 'keydown', 'touchstart'].forEach(function (ev) {
        slider.addEventListener(ev, function () { touched = true; });
      });

      slider.addEventListener('input', function () {
        if (!touched) { slider.value = String(c.get()); return; }
        c.set(parseFloat(slider.value));
        label.innerHTML = c.label + ' <b>' + c.get() + c.unit + '</b>';
        build();
        render();
      });

      row.appendChild(label);
      row.appendChild(slider);
      panel.appendChild(row);
    });

    var hint = document.createElement('div');
    hint.textContent = 'Copy these into the section settings to keep them';
    hint.style.cssText = 'font-size:11px;opacity:.6';
    panel.appendChild(hint);
    host.appendChild(panel);
    tunePanel = panel;
    syncTuneVisibility();
  }

  } catch (err) {
    /* Any geometry or material failure hands off rather than half-drawing. */
    if (renderer && renderer.domElement && renderer.domElement.parentNode) {
      renderer.domElement.parentNode.removeChild(renderer.domElement);
    }
    fallback();
  }
})();
